import React, { FC, useEffect, useState } from 'react';
import usePaginationQueryParams from '../../hooks/use-pagination-query-params';
import { OrderBy, SortDirection } from '../../common/types';
import {
  generalNotificationContentTypes,
  NotificationContent,
} from '../../common/model/dto/notification/notification-content';
import { NotificationContentFields } from '../../firebase/document-field.enums';
import styled from 'styled-components';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import OvPagination from '../UI/atoms/OvPagination';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { PaginationItem } from '@mui/material';
import OvListTable from '../UI/atoms/OvListTable';
import OvListTableRow from '../UI/atoms/OvListTableRow';
import OvListTableHeaderField from '../UI/atoms/OvListTableHeaderField';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvNotificationContentListRow from '../UI/organisms/OvNotificationContentListRow';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvButton from '../UI/atoms/OvButton';
import { useTranslation } from 'react-i18next';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import OvNotificationContentDialog from '../UI/molecules/OvCreateNotificationsContentDialog';
import { CreateNotificationContentRequest } from '../../common/model/dto/notification/create-notification-content-request';
import {
  createNotificationContent,
  getNotificationContents,
} from '../../services/admin/admin-notification-content.service';
import OvNoContent from '../UI/molecules/OvNoContent';
import { FilterModel } from '../../common/model/ui/filter.model';
import { FilterType } from '../../common/model/ui/filter.type';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import OvNotificationContentSearchFilter from '../UI/organisms/OvNotificationContentSearchFilter';
import { NotificationContentTabType } from '../../common/model/type/notification-content-tab.type';
import OvCreateSymptomLoggedEventNotificationContentDialog from '../UI/molecules/OvCreateSymptomLoggedEventNotificationContentDialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import queryString from 'query-string';

const NOTIFICATION_CONTENTS_LIST_LIMIT = 10;

const NotificationContentsList: FC<{
  useAxiosParams: any;
  tabType: NotificationContentTabType;
}> = ({ useAxiosParams, tabType }) => {
  const { t } = useTranslation();
  const [isGeneralDialogOpen, setIsGeneralDialogOpen] =
    useState<boolean>(false);
  const [isSymptomLoggedEventDialogOpen, setIsSymptomLoggedEventDialogOpen] =
    useState<boolean>(false);
  const { offset, orderBy, orderDir, setOffset, setOrderBy, setOrderDir } =
    usePaginationQueryParams();
  const [isPublished, setIsPublished] = useQueryParam(
    'is_published',
    withDefault(StringParam, '')
  );
  const [category, setCategory] = useQueryParam(
    'category',
    withDefault(StringParam, '')
  );

  const filters: FilterModel[] = [
    {
      fieldName: 'is_published',
      label: 'notificationContents.fields.isPublished',
      type: FilterType.radio,
      options: [
        {
          label: 'common.actions.yes',
          value: true,
        },
        {
          label: 'common.actions.no',
          value: false,
        },
        {
          label: 'common.actions.notSelected',
          value: '',
        },
      ],
      value: isPublished,
    },
    {
      fieldName: 'category',
      label: 'notifications.fields.category',
      type: FilterType.select,
      options: Object.values(generalNotificationContentTypes).map(
        (category) => ({
          label: category,
          value: category,
        })
      ),
      value: category,
    },
  ];

  const query = queryString.stringify({
    ...{
      limit: NOTIFICATION_CONTENTS_LIST_LIMIT,
      offset: offset ?? 0,
      order_by: orderBy ? orderBy : 'created_at',
      order_dir: orderDir ? orderDir : 'desc',
      category,
      is_published: isPublished,
    },
    ...useAxiosParams,
  });

  const {
    data: paginatedNotificationContents,
    isPending: isGetNotificationContentsPending,
    isFetching: isGetNotificationContentsFetching,
    error: getNotificationContentsError,
    refetch,
  } = useQuery({
    queryKey: ['admin-notification-contents', query],
    queryFn: () => getNotificationContents(query),
    retry: 3,
    enabled: !!orderBy && !!orderDir,
  });

  useEffect(() => {
    setOffset(0);
    setOrderBy('created_at');
    setOrderDir('desc');
  }, [setOrderDir, setOrderBy, setOffset]);

  const notificationContentList: NotificationContent[] =
    paginatedNotificationContents?.docs ?? [];
  const total = paginatedNotificationContents?.total ?? 0;

  const pageCount: () => number = () =>
    Math.ceil(
      (paginatedNotificationContents?.total ?? 0) /
        NOTIFICATION_CONTENTS_LIST_LIMIT
    );

  const goToPage = (event: any, value: number) => {
    setOffset((value - 1) * NOTIFICATION_CONTENTS_LIST_LIMIT);
  };

  const getCurrentSortDirection = (
    columnName: NotificationContentFields
  ): SortDirection =>
    columnName === orderBy ? (orderDir as SortDirection) : 'none';

  const onSortByColumn = (orderBy: OrderBy) => {
    setOrderBy(orderBy.order_by);
    setOrderDir(orderBy.order_dir);
  };

  const {
    mutate: createNotificationContentMutation,
    isPending: isCreateNotificationContentPending,
    error: createNotificationContentError,
  } = useMutation({
    mutationFn: (
      createNotificationContentRequest: CreateNotificationContentRequest
    ) => createNotificationContent(createNotificationContentRequest),
    onSuccess: () => {
      refetch();
    },
  });

  const onFilterChange = (filter: FilterModel) => {
    switch (filter.fieldName) {
      case 'is_published':
        setOffset(0);
        setIsPublished(filter.value);
        break;

      case 'category':
        setOffset(0);
        setCategory(filter.value);
        break;

      default:
        break;
    }
  };

  const hasAppliedListFilters = (): boolean => {
    return filters?.some(({ value }) => value !== '');
  };

  const onFilterClear = () => {
    setIsPublished(null);
    setCategory(null);
  };

  const isLoading =
    isGetNotificationContentsFetching ||
    isGetNotificationContentsPending ||
    isCreateNotificationContentPending;

  const handleAddNewClick = () => {
    switch (tabType) {
      case NotificationContentTabType.SYMPTOMS:
        setIsSymptomLoggedEventDialogOpen(true);
        break;

      default:
        setIsGeneralDialogOpen(true);
        break;
    }
  };

  return (
    <Container>
      <CenterPane>
        <HeaderItems>
          <ActionBar>
            <PaginationWrapper>
              {total > NOTIFICATION_CONTENTS_LIST_LIMIT && (
                <OvPagination
                  page={(offset ?? 0) / NOTIFICATION_CONTENTS_LIST_LIMIT + 1}
                  onChange={goToPage}
                  count={pageCount()}
                  renderItem={(item) => (
                    <PaginationItem
                      components={{ previous: ArrowBack, next: ArrowForward }}
                      {...item}
                    />
                  )}
                  shape="rounded"
                />
              )}
            </PaginationWrapper>
          </ActionBar>

          <StyledOvButton onClick={() => handleAddNewClick()}>
            <AddCircleIcon style={{ marginRight: '5' }} />
            {t('common.actions.addNew')}
          </StyledOvButton>
        </HeaderItems>

        {getNotificationContentsError && (
          <OvErrorMessage message={getNotificationContentsError.message} />
        )}
        {createNotificationContentError && (
          <OvErrorMessage message={createNotificationContentError.message} />
        )}

        <OvListTableWrapper>
          <OvListTable>
            <OvListTableRow>
              <OvListTableHeaderField
                sortDirection={getCurrentSortDirection(
                  NotificationContentFields.title
                )}
                sortBy={NotificationContentFields.title}
                onSort={onSortByColumn}
              >
                {t('notificationContents.fields.title')}
              </OvListTableHeaderField>
              <OvListTableHeaderField
                sortDirection={getCurrentSortDirection(
                  NotificationContentFields.category
                )}
                sortBy={NotificationContentFields.category}
                onSort={onSortByColumn}
              >
                {t('notificationContents.fields.category')}
              </OvListTableHeaderField>

              <OvListTableHeaderField>
                {t('notificationContents.fields.isPublished')}
              </OvListTableHeaderField>
              <OvListTableHeaderField>
                {t('notificationContents.fields.numberOfCreatedNotifications')}
              </OvListTableHeaderField>
              <OvListTableHeaderField
                sortDirection={getCurrentSortDirection(
                  NotificationContentFields.created_at
                )}
                sortBy={NotificationContentFields.created_at}
                onSort={onSortByColumn}
              >
                {t('notificationContents.fields.createdAt')}
              </OvListTableHeaderField>
              <OvListTableHeaderField>
                {t('common.action')}
              </OvListTableHeaderField>
            </OvListTableRow>

            {!isLoading &&
              notificationContentList.map((notificationContent) => (
                <OvListTableRow key={notificationContent.id}>
                  <OvNotificationContentListRow
                    notificationContent={notificationContent}
                  />
                </OvListTableRow>
              ))}
          </OvListTable>
        </OvListTableWrapper>

        {isLoading && <OvLoadingIndicator position="fixed" />}

        {!notificationContentList?.length && !isLoading ? (
          <OvNoContent>{t('common.noContent')}</OvNoContent>
        ) : null}

        {tabType !== NotificationContentTabType.SYMPTOMS && (
          <OvNotificationContentDialog
            tabType={tabType}
            isOpen={isGeneralDialogOpen}
            onCancel={() => setIsGeneralDialogOpen(false)}
            onSave={createNotificationContentMutation}
          />
        )}

        {tabType === NotificationContentTabType.SYMPTOMS && (
          <OvCreateSymptomLoggedEventNotificationContentDialog
            isOpen={isSymptomLoggedEventDialogOpen}
            onCancel={() => setIsSymptomLoggedEventDialogOpen(false)}
            onSave={createNotificationContentMutation}
          />
        )}
      </CenterPane>

      {tabType === NotificationContentTabType.GENERAL && (
        <OvNotificationContentSearchFilter
          filters={filters}
          onHandleFilterValueChange={onFilterChange}
          hasAppliedFilters={hasAppliedListFilters()}
          isOpened={true}
          removeFilters={onFilterClear}
        />
      )}
    </Container>
  );
};

export default NotificationContentsList;

const Container = styled.div`
  display: flex;
`;

const OvListTableWrapper = styled.div``;

const StyledOvButton = styled(OvButton)`
  && {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    padding: 0.125rem 0.7rem 0.125rem 0.35rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.5rem;
    &:last-child {
      margin-right: 0;
    }
  }
`;

const CenterPane = styled.div`
  flex-grow: 1;
  background-color: ${Colours.WHITE};
  border-radius: ${Variables.borderRadius.LARGE};
`;

const HeaderItems = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
`;
